import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Checkbox,
  List,
  ListItem,
  Button,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Check,
  Close,
  ChecklistRtl,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import GlassCard from '../shared/GlassCard';
import EmptyState from '../shared/EmptyState';
import { RADIUS } from '../../theme/tokens';

const MotionListItem = motion(ListItem);

// localStorage key is unchanged from original — no data-contract break
const STORAGE_KEY = 'focusModeTodos';

/**
 * TodoList
 * Restyled to use GlassCard list items and shared EmptyState.
 * Data layer (localStorage key, shape) is unchanged.
 * Keyboard-friendly: Enter submits, Delete on focused item removes.
 */
function TodoList({ showSnackbar }) {
  const theme = useTheme();
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try { setTodos(JSON.parse(saved)); } catch { /* corrupted — start fresh */ }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(todos));
  }, [todos]);

  const handleAddTodo = () => {
    if (!newTodo.trim()) return;
    setTodos([
      { id: Date.now(), text: newTodo.trim(), completed: false, createdAt: new Date().toISOString() },
      ...todos,
    ]);
    setNewTodo('');
    if (showSnackbar) showSnackbar('Task added', 'success');
  };

  const handleToggleComplete = (id) => {
    setTodos(todos.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)));
  };

  const handleDelete = (id) => {
    setTodos(todos.filter((t) => t.id !== id));
    if (showSnackbar) showSnackbar('Task removed', 'info');
  };

  const handleStartEdit = (todo) => {
    setEditingId(todo.id);
    setEditText(todo.text);
  };

  const handleSaveEdit = (id) => {
    if (!editText.trim()) return;
    setTodos(todos.map((t) => (t.id === id ? { ...t, text: editText.trim() } : t)));
    setEditingId(null);
    setEditText('');
    if (showSnackbar) showSnackbar('Task updated', 'success');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleClearCompleted = () => {
    setTodos(todos.filter((t) => !t.completed));
    if (showSnackbar) showSnackbar('Completed tasks cleared', 'info');
  };

  const completedCount = todos.filter((t) => t.completed).length;
  const totalCount = todos.length;

  return (
    <Box>
      {/* Header + progress */}
      <GlassCard sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={totalCount > 0 ? 1.5 : 0}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Tasks
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {completedCount} of {totalCount} completed
            </Typography>
          </Box>
          {completedCount > 0 && (
            <Button
              size="small"
              onClick={handleClearCompleted}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Clear done
            </Button>
          )}
        </Box>

        {totalCount > 0 && (
          <Box
            sx={{
              height: 6,
              borderRadius: RADIUS.pill,
              bgcolor: 'background.subtle',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${(completedCount / totalCount) * 100}%`,
                bgcolor: 'primary.main',
                borderRadius: RADIUS.pill,
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
        )}
      </GlassCard>

      {/* Add input */}
      <GlassCard sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Add a new task..."
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddTodo();
            }}
            inputProps={{ 'aria-label': 'New task text' }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS.md } }}
          />
          <IconButton
            onClick={handleAddTodo}
            disabled={!newTodo.trim()}
            aria-label="Add task"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            <Add />
          </IconButton>
        </Box>
      </GlassCard>

      {/* List */}
      {todos.length === 0 ? (
        <EmptyState
          icon={<ChecklistRtl />}
          title="No todos yet"
          description="Add your first task above!"
        />
      ) : (
        <GlassCard sx={{ overflow: 'hidden' }}>
          <List sx={{ p: 0 }}>
            <AnimatePresence initial={false}>
              {todos.map((todo, index) => (
                <MotionListItem
                  key={todo.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.18 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Delete' && editingId !== todo.id) handleDelete(todo.id);
                  }}
                  sx={{
                    borderBottom: index < todos.length - 1
                      ? `1px solid ${theme.palette.divider}`
                      : 'none',
                    py: 1.5,
                  }}
                >
                  {editingId === todo.id ? (
                    <Box display="flex" gap={1} flex={1}>
                      <TextField
                        fullWidth
                        size="small"
                        value={editText}
                        autoFocus
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(todo.id);
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS.md } }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleSaveEdit(todo.id)}
                        aria-label="Save edit"
                        sx={{ color: 'success.main' }}
                      >
                        <Check />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelEdit}
                        aria-label="Cancel edit"
                        sx={{ color: 'error.main' }}
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Checkbox
                        checked={todo.completed}
                        onChange={() => handleToggleComplete(todo.id)}
                        sx={{ mr: 1, color: 'primary.main' }}
                        inputProps={{ 'aria-label': `Complete "${todo.text}"` }}
                      />
                      <Box flex={1}>
                        <Typography
                          sx={{
                            textDecoration: todo.completed ? 'line-through' : 'none',
                            color: todo.completed ? 'text.secondary' : 'text.primary',
                            wordBreak: 'break-word',
                          }}
                        >
                          {todo.text}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(todo)}
                          disabled={todo.completed}
                          aria-label={`Edit "${todo.text}"`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(todo.id)}
                          aria-label={`Delete "${todo.text}"`}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </MotionListItem>
              ))}
            </AnimatePresence>
          </List>
        </GlassCard>
      )}
    </Box>
  );
}

export default TodoList;
